import React, { useEffect, useState, useCallback, useRef, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { deckService } from '../services/deckService';
import { getFileTypeInfo, formatFileSize } from '../types/deck';
import { FiFile, FiAlertCircle } from 'react-icons/fi';

const MobilePdfViewer = React.lazy(() => import('../components/decks/MobilePdfViewer'));

// Type cast icons for React 18 compatibility
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;

interface PublicDeckData {
  deck: {
    id: string;
    name: string;
    description: string | null;
    file_type: string;
    file_size: number;
    file_url: string;
  };
  share_link_id: string;
}

const PublicDeckView: React.FC = () => {
  // Both old (/deck/:token) and new (/decks/:businessSlug/:linkSlug) routes render this component.
  // The presence of linkSlug indicates the new slug-based format.
  const { token, linkSlug } = useParams<{ token?: string; linkSlug?: string }>();
  const isSlugBased = !!linkSlug;
  const identifier = linkSlug || token;

  const [deckData, setDeckData] = useState<PublicDeckData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [officeLoading, setOfficeLoading] = useState(true);
  const [officeError, setOfficeError] = useState(false);
  const viewStartTime = useRef<number>(Date.now());
  const viewId = useRef<string | null>(null);

  // Calculate current duration in seconds since page load
  const getDuration = useCallback(() => Math.floor((Date.now() - viewStartTime.current) / 1000), []);

  // Load deck data
  const loadDeck = useCallback(async () => {
    if (!identifier) return;
    try {
      setLoading(true);
      setError(null);
      const data = isSlugBased
        ? await deckService.getPublicDeckBySlug(identifier)
        : await deckService.getPublicDeck(identifier);
      setDeckData(data);
    } catch (err: any) {
      console.error('Failed to load deck:', err);
      setError(err?.message || 'This deck is not available or the link has expired.');
    } finally {
      setLoading(false);
    }
  }, [identifier, isSlugBased]);

  // Record initial view (called once after data loads)
  const recordInitialView = useCallback(async () => {
    if (!identifier || viewId.current) return;
    try {
      const result = isSlugBased
        ? await deckService.recordPublicViewBySlug(identifier, { duration_seconds: getDuration() })
        : await deckService.recordPublicView(identifier, { duration_seconds: getDuration() });
      viewId.current = result.id;
    } catch (err) {
      console.error('Failed to record view:', err);
    }
  }, [identifier, isSlugBased, getDuration]);

  // Update duration of existing view via API
  const updateDuration = useCallback(async () => {
    if (!identifier || !viewId.current) return;
    try {
      if (isSlugBased) {
        await deckService.updatePublicViewDurationBySlug(identifier, viewId.current, getDuration());
      } else {
        await deckService.updatePublicViewDuration(identifier, viewId.current, getDuration());
      }
    } catch (err) {
      console.error('Failed to update view duration:', err);
    }
  }, [identifier, isSlugBased, getDuration]);

  // Send duration update via sendBeacon (reliable during page unload)
  const sendBeaconUpdate = useCallback(() => {
    if (!identifier || !viewId.current) return;
    const url = isSlugBased
      ? deckService.getUpdateViewDurationUrlBySlug(identifier, viewId.current)
      : deckService.getUpdateViewDurationUrl(identifier, viewId.current);
    const data = JSON.stringify({ duration_seconds: getDuration() });
    navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
  }, [identifier, isSlugBased, getDuration]);

  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  // Mobile viewport detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // View tracking: initial record, periodic updates, and unload beacon
  useEffect(() => {
    if (!deckData) return;

    // Record initial view after 3 seconds
    const initialTimer = setTimeout(() => {
      recordInitialView();
    }, 3000);

    // Update duration every 30 seconds
    const intervalId = setInterval(() => {
      updateDuration();
    }, 30000);

    // Update on page visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendBeaconUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Update on page unload
    const handleBeforeUnload = () => {
      sendBeaconUpdate();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [deckData, recordInitialView, updateDuration, sendBeaconUpdate]);

  // Loading state
  if (loading) {
    return (
      <div className="su-public-deck-page">
        <div className="su-public-deck-loading">
          <div className="su-public-deck-spinner" />
          <p>Loading deck...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !deckData) {
    return (
      <div className="su-public-deck-page">
        <div className="su-public-deck-error">
          <AlertIcon className="su-public-deck-error-icon" />
          <h1>Deck Not Available</h1>
          <p>{error || 'This deck could not be found.'}</p>
          <p className="su-public-deck-error-hint">
            The link may have expired or been deactivated by the owner.
          </p>
        </div>
      </div>
    );
  }

  const { deck } = deckData;
  const fileTypeInfo = getFileTypeInfo(deck.file_type);
  const isPdf = deck.file_type === 'pdf';
  const isImage = deck.file_type === 'png' || deck.file_type === 'jpg';
  const isOfficeDoc = ['pptx', 'ppt', 'doc', 'docx'].includes(deck.file_type);

  return (
    <div className="su-public-deck-page">
      {/* Header */}
      <div className="su-public-deck-header">
        <div className="su-public-deck-info">
          <span
            className="su-deck-file-badge"
            style={{ backgroundColor: fileTypeInfo.color }}
          >
            {fileTypeInfo.label}
          </span>
          <h1 className="su-public-deck-name">{deck.name}</h1>
          <p className="su-public-deck-size">{formatFileSize(deck.file_size)}</p>
        </div>
      </div>

      {/* Content viewer */}
      <div className="su-public-deck-viewer">
        {isPdf && (
          isMobile ? (
            <Suspense fallback={
              <div className="su-public-deck-pdf-mobile-loading">
                <div className="su-public-deck-spinner" />
                <p>Loading PDF...</p>
              </div>
            }>
              <MobilePdfViewer fileUrl={deck.file_url} title={deck.name} />
            </Suspense>
          ) : (
            <iframe
              src={`${deck.file_url}#toolbar=0&navpanes=0`}
              className="su-public-deck-pdf"
              title={deck.name}
            />
          )
        )}
        {isImage && (
          <div className="su-public-deck-image-container">
            <img
              src={deck.file_url}
              alt={deck.name}
              className="su-public-deck-image"
            />
          </div>
        )}
        {isOfficeDoc && (
          <div className="su-public-deck-office-container">
            {officeLoading && (
              <div className="su-public-deck-office-loading">
                <div className="su-public-deck-spinner" />
                <p>Loading preview...</p>
              </div>
            )}
            {officeError ? (
              <div className="su-public-deck-office-error">
                <FileIcon className="su-public-deck-file-icon" />
                <h2>Preview Unavailable</h2>
                <p>This file could not be previewed in the browser.</p>
              </div>
            ) : (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(deck.file_url)}&embedded=true`}
                className="su-public-deck-office"
                title={deck.name}
                onLoad={() => setOfficeLoading(false)}
                onError={() => { setOfficeLoading(false); setOfficeError(true); }}
              />
            )}
          </div>
        )}
        {!isPdf && !isImage && !isOfficeDoc && (
          <div className="su-public-deck-download-prompt">
            <FileIcon className="su-public-deck-file-icon" />
            <h2>Preview Unavailable</h2>
            <p>This file type cannot be previewed in the browser.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="su-public-deck-footer">
        <p>
          Shared via <a href="https://sendupdates.ai" target="_blank" rel="noopener noreferrer">SendUpdates.ai</a>
        </p>
      </div>
    </div>
  );
};

export default PublicDeckView;
